/* Somber — Made in class carousel.
   Front photo full size; neighbours fan out behind it to the screen edges,
   each step smaller and more faded.
   Auto-advances; drag scrubs each photo through the fan (growing and fading
   as it passes the front) and a release glides on with the swipe's speed,
   settling on the nearest photo. Tap a side photo to bring it forward. */
(function () {
  if (customElements.get('made-in-class-carousel')) return;

  var TAP_THRESHOLD = 8;
  var MAX_POS = 4; // ±1..3 are shown across the screen; anything further waits, hidden, at ±4
  var WIND_UP_MS = 320;
  var WIND_UP_DISTANCE = 0.14; // share of the front photo's width
  var FLING_MS = 260; // how far a release carries on: speed (px/ms) x this
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Look of each fan position (matches the data-pos rules in the CSS); drag blends between them.
  var STOPS = [
    { scale: 1, veil: 0, opacity: 1 },
    { scale: 0.8, veil: 0.3, opacity: 1 },
    { scale: 0.66, veil: 0.6, opacity: 1 },
    { scale: 0.55, veil: 0.78, opacity: 1 },
    { scale: 0.46, veil: 0.78, opacity: 0 }
  ];

  function blend(distance, key) {
    var d = Math.min(distance, MAX_POS);
    var i = Math.min(Math.floor(d), MAX_POS - 1);
    var t = d - i;
    return STOPS[i][key] + (STOPS[i + 1][key] - STOPS[i][key]) * t;
  }

  class MadeInClassCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = Array.prototype.slice.call(this.querySelectorAll('.made-in-class__slide'));
      this.active = 0;
      this.interval = (parseFloat(this.dataset.interval) || 5) * 1000;
      this.render(true);

      if (this.slides.length < 2) return;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.addEventListener('pointerdown', this.onPointerDown);
      this.addEventListener('keydown', this.onKeyDown.bind(this));
      this.addEventListener('pointerenter', function (event) {
        if (event.pointerType === 'mouse') this.pause();
      }.bind(this));
      this.addEventListener('pointerleave', function (event) {
        if (event.pointerType === 'mouse' && !this.dragging) this.play();
      }.bind(this));
      // Keyboard focus pauses; focus from a tap or click shouldn't stop autoplay.
      this.addEventListener('focusin', function () {
        if (this.matches(':focus-visible')) this.pause();
      }.bind(this));
      this.addEventListener('focusout', function () {
        if (!this.matches(':hover')) this.play();
      }.bind(this));
      // Accessibility menu "Pause animations": play() re-checks and stays paused.
      this.onA11yChange = function () { this.play(); }.bind(this);
      document.addEventListener('somber:a11y-change', this.onA11yChange);

      this.onBlockSelect = this.onBlockSelect.bind(this);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.play();
    }

    disconnectedCallback() {
      this.pause();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.onA11yChange) document.removeEventListener('somber:a11y-change', this.onA11yChange);
    }

    // Shortest signed distance from the active slide, so the loop wraps both ways.
    offsetOf(index) {
      var n = this.slides.length;
      var offset = (((index - this.active) % n) + n) % n;
      if (offset > n / 2) offset -= n;
      return offset;
    }

    // maxMove: how many places this change moves the row (a fling can move several).
    render(initial, maxMove) {
      var limit = (maxMove || 1) + 0.5;
      this.slides.forEach(function (slide, index) {
        var pos = this.offsetOf(index);
        var clamped = Math.max(-MAX_POS, Math.min(MAX_POS, pos));
        var previous = slide.dataset.pos;
        if (slide.dataset.livePos !== undefined) previous = slide.dataset.livePos;
        this.clearLive(slide);

        // On first paint, and when a slide wraps from one side to the other,
        // place it without animating (no fade-in on load, no sweep across the front).
        if (initial || (previous !== undefined && Math.abs(clamped - Number(previous)) > limit)) {
          slide.classList.add('is-jumping');
          requestAnimationFrame(function () {
            requestAnimationFrame(function () { slide.classList.remove('is-jumping'); });
          });
        }

        slide.dataset.pos = clamped;
        slide.style.setProperty('--pos', clamped);
        slide.setAttribute('aria-hidden', pos === 0 ? 'false' : 'true');
      }, this);
    }

    goTo(index, maxMove) {
      var n = this.slides.length;
      this.active = ((index % n) + n) % n;
      this.render(false, maxMove);
    }

    // Distance the row travels for one place, in px (the CSS --mic-shift share of a card).
    stepPx() {
      var shift = parseFloat(getComputedStyle(this).getPropertyValue('--mic-shift')) || 50;
      var card = this.slides[0] ? this.slides[0].offsetWidth : 0;
      return Math.max(1, (card * shift) / 100);
    }

    // While dragging: place every slide at a fractional position, blending size and fade.
    renderLive(shift) {
      var n = this.slides.length;
      // with few photos the far ends meet; fade them out before they swap sides
      var edge = n / 2;
      this.slides.forEach(function (slide, index) {
        var pos = this.offsetOf(index) + shift;
        if (pos > edge) pos -= n;
        if (pos < -edge) pos += n;
        var clamped = Math.max(-MAX_POS, Math.min(MAX_POS, pos));
        var distance = Math.abs(clamped);
        var opacity = blend(distance, 'opacity');
        if (edge < MAX_POS) opacity = Math.min(opacity, Math.max(0, (edge - Math.abs(pos)) * 2));

        slide.dataset.livePos = clamped;
        slide.dataset.pos = Math.round(clamped);
        slide.style.setProperty('--pos', clamped);
        slide.style.setProperty('--mic-scale', blend(distance, 'scale'));
        slide.style.setProperty('--mic-veil', blend(distance, 'veil'));
        slide.style.setProperty('--mic-opacity', opacity);
        slide.style.setProperty('--mic-z', 3 - Math.min(3, Math.round(distance)));
      }, this);
    }

    clearLive(slide) {
      delete slide.dataset.livePos;
      ['--mic-scale', '--mic-veil', '--mic-opacity', '--mic-z'].forEach(function (name) {
        slide.style.removeProperty(name);
      });
    }

    next() { this.goTo(this.active + 1); }
    prev() { this.goTo(this.active - 1); }

    play() {
      this.pause();
      var still = document.documentElement.classList.contains('a11y-still');
      if (reduceMotion.matches || still || this.dragging) return;
      this.timer = setInterval(this.swipeNext.bind(this), this.interval);
    }

    pause() {
      clearInterval(this.timer);
      clearTimeout(this.windTimer);
      this.timer = null;
      if (this.classList.contains('is-winding')) {
        this.classList.remove('is-winding');
        this.style.setProperty('--drag', '0px');
      }
    }

    // Autoplay advance that reads like a swipe: pull the row left, then let go.
    swipeNext() {
      var front = this.slides[this.active];
      var width = front ? front.offsetWidth : 0;
      this.classList.add('is-winding');
      this.style.setProperty('--drag', -Math.round(width * WIND_UP_DISTANCE) + 'px');
      this.windTimer = setTimeout(function () {
        this.classList.remove('is-winding');
        this.style.setProperty('--drag', '0px');
        this.next();
      }.bind(this), WIND_UP_MS);
    }

    onKeyDown(event) {
      if (event.key === 'ArrowRight') { this.next(); event.preventDefault(); }
      if (event.key === 'ArrowLeft') { this.prev(); event.preventDefault(); }
    }

    onPointerDown(event) {
      if (event.button !== 0) return;
      this.dragging = true;
      this.startX = event.clientX;
      this.deltaX = 0;
      this.step = this.stepPx();
      this.samples = [{ x: event.clientX, t: event.timeStamp }];
      this.pointerType = event.pointerType;
      this.pointerTarget = event.target.closest('.made-in-class__slide');
      this.pause();
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      this.deltaX = event.clientX - this.startX;
      this.samples.push({ x: event.clientX, t: event.timeStamp });
      if (this.samples.length > 6) this.samples.shift();
      if (!this.classList.contains('is-dragging')) {
        if (Math.abs(this.deltaX) < TAP_THRESHOLD) return;
        this.classList.add('is-dragging');
      }
      // dragging right brings earlier photos forward: positions move with the finger
      this.renderLive(this.deltaX / this.step);
    }

    // Finger speed over the last ~100ms, in px/ms.
    velocity() {
      var last = this.samples[this.samples.length - 1];
      var first = this.samples[0];
      for (var i = this.samples.length - 1; i >= 0; i--) {
        if (last.t - this.samples[i].t > 100) break;
        first = this.samples[i];
      }
      var dt = last.t - first.t;
      return dt > 0 ? (last.x - first.x) / dt : 0;
    }

    onPointerUp() {
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      this.dragging = false;
      var dragged = this.classList.contains('is-dragging');
      this.classList.remove('is-dragging');

      if (dragged) {
        // carry on with the swipe's speed, then settle on the nearest photo
        var travel = this.deltaX + this.velocity() * FLING_MS;
        var half = Math.floor((this.slides.length - 1) / 2);
        var moves = Math.max(-half, Math.min(half, Math.round(-travel / this.step)));
        var landed = Math.abs(moves - -this.deltaX / this.step);
        this.goTo(this.active + moves, Math.ceil(landed) + 1);
      } else if (this.pointerTarget) {
        // A tap on a side photo brings it to the front.
        var index = this.slides.indexOf(this.pointerTarget);
        if (index !== -1 && index !== this.active) this.goTo(index, Math.abs(this.offsetOf(index)));
      }

      if (this.pointerType !== 'mouse' || !this.matches(':hover')) this.play();
    }

    onBlockSelect(event) {
      var index = this.slides.findIndex(function (slide) {
        return slide.dataset.blockId === event.detail.blockId;
      });
      if (index === -1) return;
      this.pause();
      this.goTo(index);
    }
  }

  customElements.define('made-in-class-carousel', MadeInClassCarousel);
})();
